import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Components
import Sidebar from '../components/Sidebar';
import AllChart from '../components/graphsConfig/AllChart';
import ChambreDetails from '../components/chambreConfig/ChambreDetails';
import AlarmesModal from '../components/chambreConfig/AlarmesModal';

// Icons
import { PiHouseLight } from 'react-icons/pi';

// Firebase :
import { getDocs, where, query } from 'firebase/firestore';
import { dataCollection, porteLogsCollection } from '../config/firebaseConfig';
import { Box } from '@mui/material';
import { LiaTimesSolid } from 'react-icons/lia';
import { TbAlertTriangle, TbClock, TbDatabaseOff } from 'react-icons/tb';

const ChambreConfig = () => {
    const [co2, setCo2] = useState([]);
    const [hum, setHum] = useState([]);
    const [temp, setTemp] = useState([]);
    const [eth, setEth] = useState([]);
    const [annotation, setAnnotation] = useState([]);


    const location = useLocation();
    const { state } = location;
    const { room, roomName, id, realTimeName } = state || [];

    // ? ============== Getting Chart Data ==============
    const getChartData = async (room_id) => {
        try {

            const now = new Date();
            const startOfTheDay = new Date();
            startOfTheDay.setHours(0, 0, 0, 0);

            const convertedDateStart = new Date(startOfTheDay).getTime();
            const convertedDateEnd = new Date(now).getTime();


            const q = query(
                dataCollection,
                where('time', '>=', Number(convertedDateStart)),
                where('time', '<=', Number(convertedDateEnd)),
                where('chambre', '==', String(room_id))
            );

            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map((doc) => doc.data());

            const q1 = query(
                porteLogsCollection,
                where("date", ">=", Number(convertedDateStart)),
                where("date", "<=", Number(convertedDateEnd)),
                where("chambre", "==", String(room_id))
            );
            const querySnapshotPorts = await getDocs(q1);
            const portsData = querySnapshotPorts.docs.map((doc) => doc.data());

            const processedPortsData = processPortsData(portsData);
            const processedData = processData(data);

            setCo2(processedData.co2);
            setHum(processedData.humidite);
            setTemp(processedData.temperature);
            setEth(processedData.ethylene);
            setAnnotation(processedPortsData);
        }
        catch (error) {
            console.log('Error in getting chart data (getChartData) :', error)
        }
    };

    const processData = (data) => {

        let co2Arr = [];
        let humiditeArr = [];
        let ethyleneArr = [];
        let temperatureArr = [];

        for (let n = 0; n < data.length; n++) {
            co2Arr.push([data[n].time, data[n].co2]);
            humiditeArr.push([data[n].time, data[n].humidite]);
            ethyleneArr.push([data[n].time, data[n].ethylene]);
            temperatureArr.push([data[n].time, data[n].temperature]);

        }

        // Generate timestamps for the remaining part of the day
        if (data.length > 0) {
            const lastTimestamp = data[data.length - 1].time;
            const theRemainedOfTheDay = generateTimestamps(lastTimestamp);

            theRemainedOfTheDay.forEach((timestamp) => {
                co2Arr.push([timestamp, null]);
                humiditeArr.push([timestamp, null]);
                ethyleneArr.push([timestamp, null]);
                temperatureArr.push([timestamp, null]);
            });
        }

        return {
            co2: co2Arr,
            humidite: humiditeArr,
            ethylene: ethyleneArr,
            temperature: temperatureArr,
        };
    }

    const processPortsData = (portsData) => {
        let newArr = [];
        try {
            if (portsData.length === 0) {
                return [];
            }
            let portsOnOffArr = portsData.map((port) => [port.date, port.status]);

            if (portsOnOffArr[0][1] === "off") {
                portsOnOffArr.shift();
            }
            if (portsOnOffArr.length % 2 !== 0) {
                portsOnOffArr.pop();
            }
            for (let i = 2; i <= portsOnOffArr.length; i += 2) {
                let obj = {
                    x: portsOnOffArr[i - 2][0],
                    x2: portsOnOffArr[i - 1][0],
                    fillColor: "#F39C12",
                };
                newArr.push(obj);
            }
            return newArr;
        } catch (error) {
            return [];
        }
    };

    function generateTimestamps(initialTimestamp) {

        // Ensure the initial timestamp is treated as milliseconds
        const timestamps = [];
        const date = new Date(initialTimestamp);
        const endOfDay = new Date(date);

        // Set endOfDay to 11:59:59 PM of the same day
        endOfDay.setHours(23, 59, 59, 999);

        let currentTimestamp = initialTimestamp;

        while (currentTimestamp <= endOfDay.getTime()) {
            timestamps.push(currentTimestamp);
            currentTimestamp += 300 * 1000; // Add 5 minutes (300 seconds in milliseconds)
        }

        return timestamps;
    }

    useEffect(() => {
        getChartData(id);
    }, []);

    function convertTimestampToFrenchDate(timestamp) {
        const date = new Date(timestamp);

        const options = {
            year: "numeric",
            month: "long",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false, // 24-hour format
        };

        return date.toLocaleDateString("fr-FR", options);
    }

    return (
        <div className="flex lg:gap-5 bg-[#fffcf6]">
            <Sidebar />
            <main className="flex-1 mx-auto py-4 pr-4 pl-4 lg:pl-0 h-screen overflow-y-scroll">
                <p className='text-4xl lg:text-5xl font-bold mb-4 text-center md:text-left'>{roomName}</p>

                <div className="flex justify-between items-center mb-4">
                    <div className='font-medium flex items-center text-sm lg:text-base'><Link to='/dashboard' className='mr-1 hover:underline text-primary cursor-pointer flex items-center'><PiHouseLight className='mr-1' size={22} /> Dashbaord</Link> {'>'} {roomName}</div>

                </div>

                <div className='flex justify-between items-start'>
                    <div className='flex flex-col'>
                        <ChambreDetails room={room} name={realTimeName} />
                        {
                            room?.Alarmes?.length > 0
                                ? <Box className={`${room?.Alarmes?.length >= 4 ? 'h-[363px] overflow-x-scroll' : 'overflow-auto'} bg-white rounded-md p-2 shadow-md mb-4 w-full`}>
                                    <div className="flex justify-between items-center mb-4">
                                        <p className='text-2xl font-bold'>Alarmes</p>
                                    </div>
                                    {
                                        room?.Alarmes?.map((alarme) => (
                                            <div className='border border-[red] m-2 rounded-md'>
                                                <p className='font-medium p-1 flex items-center' key={alarme?.id}>
                                                    <TbAlertTriangle size={24} className='mr-4 text-[red]' />
                                                    {alarme?.message}
                                                </p>
                                                <p className='font-medium p-1 flex items-center'>
                                                    <TbClock size={24} className='mr-4 text-[red]' />
                                                    {convertTimestampToFrenchDate(alarme?.Date)}
                                                </p>
                                            </div>
                                        ))
                                    }
                                </Box>
                                : <Box className={`${room?.Alarmes?.length >= 4 ? 'h-[365px] overflow-x-scroll' : 'overflow-auto'} bg-white rounded-md p-2 shadow-md mb-4 w-full`}>
                                    <div className="flex justify-between items-center mb-4">
                                        <p className='text-2xl font-bold'>Alarmes</p>
                                    </div>
                                    <div className='flex justify-center items-center text-gray mb-4'>
                                        <TbDatabaseOff className='mr-2' size={22} />
                                        <p>Aucune alarme trouvée</p>
                                    </div>
                                </Box>
                        }
                    </div>
                    <div className=' w-full md:w-2/3 ml-2'>
                        <AllChart temperature={temp} humidite={hum} co2={co2} ethylene={eth} annotation={annotation} name={`Les données de cette journée (24h)`} />
                    </div>
                </div>
            </main>
        </div>
    )
}

export default ChambreConfig